"use client";

import {
  useEffect,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
} from "react";

import type { LazyPopoverImpl as LazyPopoverImplType } from "./popover-impl.client";
import { useRefocusOnSwap } from "./trigger-props";

type ImplProps = Parameters<typeof LazyPopoverImplType>[0];

/**
 * Floating-UI-free until interaction. Renders `renderTrigger` as a plain SSR'd
 * element (no Base UI, no @floating-ui). The real Base-UI Popover — and its
 * Floating UI chunk — is `import()`ed only on the first hover / focus / click,
 * and the standalone trigger stays mounted until that chunk has fully loaded
 * into state, so the trigger never blinks out (no gap, no missed click, no CLS).
 * Keeps ~38 KB of Floating UI out of the eager route bundle that gates LCP.
 */
export function LazyPopover({
  open,
  onOpenChange,
  renderTrigger,
  contentClassName,
  align = "center",
  side,
  sideOffset,
  collisionAvoidance,
  children,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderTrigger: (props: Record<string, unknown>) => ReactElement;
  contentClassName?: string;
  align?: "start" | "center" | "end";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  collisionAvoidance?: ImplProps["collisionAvoidance"];
  children: ReactNode;
}) {
  const [Impl, setImpl] = useState<ComponentType<ImplProps> | null>(null);
  // Uncontrolled fallback: when the caller passes no `open`, we still need to
  // open on the click that armed it (Base UI owns it once mounted).
  const [selfOpen, setSelfOpen] = useState(false);
  const controlled = open !== undefined;
  const effectiveOpen = controlled ? open : selfOpen;
  const handleOpenChange = controlled ? onOpenChange : setSelfOpen;

  // A cold click (no prior hover/focus warm-up, e.g. touch) arms the import
  // AND wants to open in the same gesture — but the Popover can't mount until
  // the chunk resolves. Setting `open` true immediately would mount it
  // already-open with no real closed→open transition, so Base UI never shows
  // it: the click looks like a no-op until a second click (chunk now cached)
  // triggers a real transition. Deferring the open to the effect below lets
  // the first click work: mount closed, then transition open once ready.
  const [pendingOpen, setPendingOpen] = useState(false);
  const refocus = useRefocusOnSwap();

  const arm = () => {
    if (Impl) return;
    void import("./popover-impl.client").then((m) =>
      setImpl(() => m.LazyPopoverImpl as ComponentType<ImplProps>),
    );
  };

  useEffect(() => {
    if (!Impl || !pendingOpen) return;
    setPendingOpen(false);
    handleOpenChange?.(true);
  }, [Impl, pendingOpen, handleOpenChange]);

  if (!Impl) {
    return renderTrigger(
      refocus({
        onPointerEnter: arm,
        onFocus: arm,
        onClick: () => {
          setPendingOpen(true);
          arm();
        },
        "aria-haspopup": "dialog",
        "aria-expanded": Boolean(effectiveOpen),
      }),
    );
  }

  return (
    <Impl
      open={effectiveOpen}
      onOpenChange={handleOpenChange}
      renderTrigger={(props) => renderTrigger(refocus(props))}
      contentClassName={contentClassName}
      align={align}
      side={side}
      sideOffset={sideOffset}
      collisionAvoidance={collisionAvoidance}
    >
      {children}
    </Impl>
  );
}
